import { RotateCw } from 'lucide-react';
import type { ReactElement } from 'react';
import { hasActiveStoreFilters, type StoreProductsParams } from '@/entities/product';
import { buttonVariants } from '@/shared/ui';
import { ColorFamilyFilter } from './color-family-filter';
import { FilterSection } from './filter-section';
import { MinOrderQtyFilter } from './min-order-qty-filter';
import { UnitPriceFilter } from './unit-price-filter';

export interface StoreFilterPanelProps {
  readonly filters: StoreProductsParams;
  readonly onFiltersChange: (filters: StoreProductsParams) => void;
}

/** 초기화 — 36px 원(호버 민트 채움·비활성 50% 불투명도·포커스 링 포함). */
const RESET_BUTTON_CLASS_NAME = buttonVariants({ variant: 'ghost', size: 'icon' });

/**
 * 필터 내용 — "필터 검색" 헤더(초기화) 아래 접이식 섹션.
 * 색상은 펼친 채, 수량·단가는 접은 채 시작한다. 정렬은 툴바, 카테고리는 목록 위 탭(store-category-tabs) 소관이라
 * 여기서 다루지 않는다. 모든 폭에서 툴바의 "필터" 버튼이 여는 오른쪽 드로어가 이 내용을 담는다.
 */
export const StoreFilterPanel = ({
  filters,
  onFiltersChange,
}: StoreFilterPanelProps): ReactElement => (
  <div className="text-sm">
    <div className="flex h-14 items-center justify-between border-b border-foreground">
      <h2 className="text-xl font-bold tracking-[-0.02em]">필터 검색</h2>
      <button
        type="button"
        aria-label="필터 초기화"
        title="필터 초기화"
        disabled={!hasActiveStoreFilters(filters)}
        // 정렬·페이지는 필터가 아니다 — 남기고, 페이지 되돌림은 페이지 컴포넌트가 한다.
        onClick={() => onFiltersChange({ sort: filters.sort, page: filters.page })}
        className={RESET_BUTTON_CLASS_NAME}
      >
        <RotateCw aria-hidden className="size-4" strokeWidth={1.5} />
      </button>
    </div>

    <FilterSection title="색상" defaultOpen>
      <ColorFamilyFilter
        selected={filters.colorFamilies ?? []}
        onChange={(colorFamilies) =>
          onFiltersChange({
            ...filters,
            colorFamilies: colorFamilies.length > 0 ? colorFamilies : undefined,
          })
        }
      />
    </FilterSection>

    <FilterSection title="최소 주문 수량">
      <MinOrderQtyFilter
        value={filters.minOrderQtyMax}
        onChange={(minOrderQtyMax) => onFiltersChange({ ...filters, minOrderQtyMax })}
      />
    </FilterSection>

    <FilterSection title="1개당 단가(원)">
      <UnitPriceFilter
        min={filters.unitPriceMin}
        max={filters.unitPriceMax}
        onChange={({ min, max }) =>
          onFiltersChange({ ...filters, unitPriceMin: min, unitPriceMax: max })
        }
      />
    </FilterSection>
  </div>
);
